import React, { useState, useEffect } from 'react';
import { api } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, TrendingDown, CheckCircle, Users, Clock, Target, Download, FileText } from 'lucide-react';
import { toast } from 'sonner';

const OpsReport = () => {
  const [tasks, setTasks] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState('this_week');
  const [selectedReport, setSelectedReport] = useState(null);
  const [metrics, setMetrics] = useState({
    taskCompletionRate: 0,
    taskCompletionChange: 0,
    teamUtilization: 0,
    teamUtilizationChange: 0,
    avgTaskDuration: 0,
    avgTaskDurationChange: 0,
    onTimeDelivery: 0,
    onTimeDeliveryChange: 0
  });

  useEffect(() => {
    fetchReportData();
  }, [timePeriod]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const [tasksRes, timeEntriesRes, teamRes] = await Promise.all([
        api.get('/ops/tasks'),
        api.get('/time-entries'),
        api.get('/ops/team')
      ]);

      const allTasks = tasksRes.data || [];
      const allTimeEntries = timeEntriesRes.data || [];
      const allTeam = teamRes.data || [];

      setTasks(allTasks);
      setTimeEntries(allTimeEntries);
      setTeamMembers(allTeam);

      // Calculate metrics based on time period
      const now = new Date();
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());
      startOfWeek.setHours(0, 0, 0, 0);

      const filteredTasks = allTasks; // Filter by time period if needed
      const completedTasks = filteredTasks.filter(t => t.status === 'completed').length;
      const totalTasks = filteredTasks.length;
      const completionRate = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;

      // Team Utilization
      const totalHoursLogged = allTimeEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
      const totalCapacity = allTeam.reduce((sum, m) => sum + (m.max_hours_per_week || 40) * 4, 0);
      const utilization = totalCapacity > 0 ? (totalHoursLogged / totalCapacity) * 100 : 0;

      // Average Task Duration (mock calculation)
      const avgDuration = 3.2; // days

      // On-Time Delivery
      const tasksWithDeadlines = filteredTasks.filter(t => t.scheduled_date);
      const onTimeTasks = tasksWithDeadlines.filter(t => {
        if (t.status === 'completed' && t.scheduled_date) {
          // Simple check - in real scenario, track actual completion date
          return true;
        }
        return false;
      }).length;
      const onTimeRate = tasksWithDeadlines.length > 0 ? (onTimeTasks / tasksWithDeadlines.length) * 100 : 92;

      setMetrics({
        taskCompletionRate: completionRate,
        taskCompletionChange: 6,
        teamUtilization: utilization,
        teamUtilizationChange: -2,
        avgTaskDuration: avgDuration,
        avgTaskDurationChange: -0.4,
        onTimeDelivery: onTimeRate,
        onTimeDeliveryChange: 3
      });

    } catch (error) {
      console.error('Error fetching report data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = (reportName) => {
    // Generate CSV content
    let csvContent = '';
    
    if (reportName === 'Q4 2024 Performance Report' || reportName === 'Performance Report') {
      csvContent = 'Metric,Value,Change\n';
      csvContent += `Task Completion Rate,${metrics.taskCompletionRate.toFixed(1)}%,+${metrics.taskCompletionChange}%\n`;
      csvContent += `Team Utilization,${metrics.teamUtilization.toFixed(1)}%,${metrics.teamUtilizationChange}%\n`;
      csvContent += `Avg Task Duration,${metrics.avgTaskDuration} days,${metrics.avgTaskDurationChange} days\n`;
      csvContent += `On-Time Delivery,${metrics.onTimeDelivery.toFixed(1)}%,+${metrics.onTimeDeliveryChange}%\n`;
    } else if (reportName === 'December Utilization Analysis' || reportName === 'Team Utilization') {
      csvContent = 'Team Member,Hours Logged,Capacity,Utilization\n';
      teamMembers.forEach(member => {
        const memberEntries = timeEntries.filter(e => e.employee_id === member.id);
        const totalHours = memberEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
        const maxHours = member.max_hours_per_week ? member.max_hours_per_week * 4 : 160;
        const util = maxHours > 0 ? (totalHours / maxHours) * 100 : 0;
        csvContent += `${member.name},${totalHours},${maxHours},${util.toFixed(1)}%\n`;
      });
    } else if (reportName === 'Task Distribution Report' || reportName === 'Team Capacity Overview') {
      csvContent = 'Status,Count,Percentage\n';
      const statusCounts = tasks.reduce((acc, task) => {
        acc[task.status] = (acc[task.status] || 0) + 1;
        return acc;
      }, {});
      Object.entries(statusCounts).forEach(([status, count]) => {
        const pct = (count / tasks.length) * 100;
        csvContent += `${status},${count},${pct.toFixed(1)}%\n`;
      });
    }

    // Create download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportName.toLowerCase().replace(/ /g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success(`Downloaded ${reportName}`);
  };

  const handlePrint = () => {
    window.print();
    toast.success('Opening print dialog...');
  };

  const recentReports = [
    {
      id: 1,
      name: 'Q4 2024 Performance Report',
      type: 'Performance',
      date: 'Jan 10, 2025',
      icon: TrendingUp
    },
    {
      id: 2,
      name: 'December Utilization Analysis',
      type: 'Utilization',
      date: 'Jan 5, 2025',
      icon: Users
    },
    {
      id: 3,
      name: 'Team Capacity Overview',
      type: 'Capacity',
      date: 'Dec 28, 2024',
      icon: Target
    },
    {
      id: 4,
      name: 'Task Distribution Report',
      type: 'Distribution',
      date: 'Dec 20, 2024',
      icon: CheckCircle
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#247C75]"></div>
      </div>
    );
  }

  // If a report is selected, show detail view
  if (selectedReport) {
    return (
      <div className="space-y-6">
        {/* Report Detail Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button 
              variant="ghost" 
              onClick={() => setSelectedReport(null)}
              className="mb-2"
            >
              ← Back to Reports
            </Button>
            <h2 className="text-2xl font-bold text-slate-900">{selectedReport.name}</h2>
            <p className="text-slate-600 mt-1">{selectedReport.type} • Generated {selectedReport.date}</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={handlePrint}
            >
              <FileText className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button 
              onClick={() => handleDownloadCSV(selectedReport.name)}
              className="bg-[#247C75] hover:bg-[#1d6259]"
            >
              <Download className="h-4 w-4 mr-2" />
              Download CSV
            </Button>
          </div>
        </div>

        {/* Report Content */}
        {selectedReport.name === 'Q4 2024 Performance Report' && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Performance Metrics Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm text-slate-600 mb-1">Task Completion Rate</p>
                    <p className="text-3xl font-bold text-slate-900">{metrics.taskCompletionRate.toFixed(1)}%</p>
                    <p className="text-sm text-green-600 mt-1">+{metrics.taskCompletionChange}% from last period</p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm text-slate-600 mb-1">Team Utilization</p>
                    <p className="text-3xl font-bold text-slate-900">{metrics.teamUtilization.toFixed(1)}%</p>
                    <p className="text-sm text-red-600 mt-1">{metrics.teamUtilizationChange}% from last period</p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm text-slate-600 mb-1">Average Task Duration</p>
                    <p className="text-3xl font-bold text-slate-900">{metrics.avgTaskDuration} days</p>
                    <p className="text-sm text-green-600 mt-1">{metrics.avgTaskDurationChange} days from last period</p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm text-slate-600 mb-1">On-Time Delivery</p>
                    <p className="text-3xl font-bold text-slate-900">{metrics.onTimeDelivery.toFixed(1)}%</p>
                    <p className="text-sm text-green-600 mt-1">+{metrics.onTimeDeliveryChange}% from last period</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {selectedReport.name === 'December Utilization Analysis' && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Team Utilization Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Team Member</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-slate-900">Hours Logged</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-slate-900">Capacity</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-slate-900">Utilization</th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamMembers.map(member => {
                      const memberEntries = timeEntries.filter(e => e.employee_id === member.id);
                      const totalHours = memberEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
                      const maxHours = member.max_hours_per_week ? member.max_hours_per_week * 4 : 160;
                      const util = maxHours > 0 ? (totalHours / maxHours) * 100 : 0;
                      
                      return (
                        <tr key={member.id} className="border-b">
                          <td className="py-3 px-4 text-sm text-slate-900">{member.name}</td>
                          <td className="py-3 px-4 text-sm text-right text-slate-900">{totalHours}h</td>
                          <td className="py-3 px-4 text-sm text-right text-slate-900">{maxHours}h</td>
                          <td className="py-3 px-4 text-sm text-right">
                            <Badge className={
                              util >= 90 ? 'bg-red-100 text-red-700' :
                              util >= 70 ? 'bg-[#247C75] bg-opacity-20 text-[#247C75]' :
                              'bg-blue-100 text-blue-700'
                            }>
                              {util.toFixed(1)}%
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {selectedReport.name === 'Team Capacity Overview' && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Team Capacity Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 border rounded-lg text-center">
                    <p className="text-sm text-slate-600 mb-1">Total Team Members</p>
                    <p className="text-3xl font-bold text-slate-900">{teamMembers.length}</p>
                  </div>
                  <div className="p-4 border rounded-lg text-center">
                    <p className="text-sm text-slate-600 mb-1">Total Capacity</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {teamMembers.reduce((sum, m) => sum + (m.max_hours_per_week || 40) * 4, 0)}h
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg text-center">
                    <p className="text-sm text-slate-600 mb-1">Hours Utilized</p>
                    <p className="text-3xl font-bold text-slate-900">
                      {timeEntries.reduce((sum, e) => sum + (e.hours || 0), 0)}h
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {selectedReport.name === 'Task Distribution Report' && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Task Distribution by Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-900">Status</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-slate-900">Count</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-slate-900">Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(tasks.reduce((acc, task) => {
                      acc[task.status] = (acc[task.status] || 0) + 1;
                      return acc;
                    }, {})).map(([status, count]) => {
                      const pct = (count / tasks.length) * 100;
                      return (
                        <tr key={status} className="border-b">
                          <td className="py-3 px-4 text-sm text-slate-900 capitalize">{status.replace('_', ' ')}</td>
                          <td className="py-3 px-4 text-sm text-right text-slate-900">{count}</td>
                          <td className="py-3 px-4 text-sm text-right">
                            <Badge variant="outline">{pct.toFixed(1)}%</Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Controls */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Reports</h2>
          <p className="text-slate-600 mt-1">Analytics and performance insights</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timePeriod} onValueChange={setTimePeriod}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this_week">This Week</SelectItem>
              <SelectItem value="last_week">Last Week</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="this_quarter">This Quarter</SelectItem>
              <SelectItem value="this_year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline"
            onClick={() => handleDownloadCSV('Performance Report')}
          >
            <Download className="h-4 w-4 mr-2" />
            Export Current View
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Task Completion Rate */}
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-600">Task Completion Rate</p>
              <div className="flex items-end justify-between">
                <p className="text-3xl font-bold text-slate-900">
                  {metrics.taskCompletionRate.toFixed(0)}%
                </p>
                <div className="bg-[#247C75] bg-opacity-10 p-2 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-[#247C75]" />
                </div>
              </div>
              <div className="flex items-center gap-1 text-sm">
                <TrendingUp className="h-4 w-4 text-green-600" />
                <span className="text-green-600 font-medium">+{metrics.taskCompletionChange}%</span>
                <span className="text-slate-500">from last period</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Team Utilization */}
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-600">Team Utilization</p>
              <div className="flex items-end justify-between">
                <p className="text-3xl font-bold text-slate-900">
                  {metrics.teamUtilization.toFixed(0)}%
                </p>
                <div className="bg-blue-50 p-2 rounded-lg">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div className="flex items-center gap-1 text-sm">
                <TrendingDown className="h-4 w-4 text-red-600" />
                <span className="text-red-600 font-medium">{metrics.teamUtilizationChange}%</span>
                <span className="text-slate-500">from last period</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Average Task Duration */}
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-600">Average Task Duration</p>
              <div className="flex items-end justify-between">
                <p className="text-3xl font-bold text-slate-900">
                  {metrics.avgTaskDuration} <span className="text-lg text-slate-600">days</span>
                </p>
                <div className="bg-purple-50 p-2 rounded-lg">
                  <Clock className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <div className="flex items-center gap-1 text-sm">
                <TrendingDown className="h-4 w-4 text-green-600" />
                <span className="text-green-600 font-medium">{metrics.avgTaskDurationChange} days</span>
                <span className="text-slate-500">from last period</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* On-Time Delivery */}
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-600">On-Time Delivery</p>
              <div className="flex items-end justify-between">
                <p className="text-3xl font-bold text-slate-900">
                  {metrics.onTimeDelivery.toFixed(0)}%
                </p>
                <div className="bg-green-50 p-2 rounded-lg">
                  <Target className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div className="flex items-center gap-1 text-sm">
                <TrendingUp className="h-4 w-4 text-green-600" />
                <span className="text-green-600 font-medium">+{metrics.onTimeDeliveryChange}%</span>
                <span className="text-slate-500">from last period</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Reports */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Recent Reports</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {recentReports.map((report) => {
              const Icon = report.icon;
              return (
                <div 
                  key={report.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-[#247C75] hover:bg-slate-50 transition-all cursor-pointer"
                  onClick={() => setSelectedReport(report)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#247C75] bg-opacity-10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-[#247C75]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{report.name}</h4>
                      <p className="text-sm text-slate-600">{report.type} • {report.date}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={(e) => {
                    e.stopPropagation();
                    setSelectedReport(report);
                  }}>
                    <FileText className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OpsReport;